// ET-day window label (MIS-813 / Task 32; QF-1 / MIS-1020) — extracted from
// the analytics page so the rendering is unit-testable.
//
// Every analytics response carries an ET-day window `meta` envelope; the
// by-campaign endpoint additionally carries per-column-family `scope` flags
// (worker routes/analytics.ts) because only its funnel columns (visits/leads)
// honor the from/to window — calls/conversions/revenue attach via the
// campaign's all-time matched phones and spend is the campaign's all-time
// creative spend (deliberate; see AnalyticsRepo.byCampaign). When those flags
// say `all_time`, the label states it explicitly so the window never
// misrepresents the calls/revenue/spend columns.

/** Per-column-family window scope flags (by-campaign meta, QF-1). */
export interface WindowScope {
  funnel: string;
  calls: string;
  spend: string;
}

/** The ET-day window envelope every analytics response carries. */
export interface WindowMeta {
  etDayStart: string;
  etDayEnd: string;
  timezone: string;
  /** Present on by-campaign responses only. */
  scope?: WindowScope;
}

/**
 * Human wording for the meta's scope flags: which column families IGNORE the
 * window. Returns null when there is no scope (or nothing is all-time), so
 * window-honest tabs (overview) render the plain label unchanged.
 */
export function scopeNote(scope?: WindowScope): string | null {
  if (!scope) return null;
  const allTime: string[] = [];
  if (scope.calls === 'all_time') allTime.push('calls', 'revenue');
  if (scope.spend === 'all_time') allTime.push('spend');
  if (allTime.length === 0) return null;
  return `${allTime.join(' / ')} ${allTime.length > 1 ? 'are' : 'is'} all-time per campaign`;
}

/** The response meta's concrete ET-day window, kept under each analytics tab. */
export function EtWindowLabel({ meta }: { meta: WindowMeta }): React.ReactElement {
  const note = scopeNote(meta.scope);
  return (
    <p className="text-xs text-gray-400">
      ET window: {meta.etDayStart} – {meta.etDayEnd} ({meta.timezone})
      {note ? <span> — {note}</span> : null}
    </p>
  );
}
